import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import ChatFilesContext from 'router/subrouters/Dash/subrouters/private/subrouters/Messaging/providers/ChatFiles/ChatFiles.context';
import { ChatSessionFile } from 'models/Message';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import { getHasElementsInArray } from 'utils';
import useChatFileMenuOptions from '../../hooks/useChatFileMenuOptions';
import { getFileMetaLine } from '../../utils';
import { ALIGNMENT } from 'models/Global';

import './ChatFileRow.styles.scss';

type ChatFileRowProps = {
  className?: string;
  chatSessionFile: ChatSessionFile;
  /**
   * `compact` is the sidebar preview — no size. `detailed` is the "Files in chat"
   * modal, which carries the file size.
   */
  variant?: 'compact' | 'detailed';
  /** Data Room access is per user: without it there is no status and nothing to save to */
  hasDataRoomAccess?: boolean;
  dataRoomId: string;
  dataRoomName: string;
  ownerId?: string;
  userSide?: QAndASides;
  onRemoved?: (fileId: string) => void;
  onSavedToDataRoom?: (fileId: string) => void;
  onOpen: (fileId: string, fileName: string) => void;
};

const ChatFileRow: React.FC<ChatFileRowProps> = (props) => {
  const {
    className,
    chatSessionFile,
    variant = 'compact',
    hasDataRoomAccess,
    dataRoomId,
    dataRoomName,
    ownerId,
    userSide,
    onRemoved,
    onSavedToDataRoom,
    onOpen,
  } = props;

  const {
    _id,
    isDataRoomFile,
    canMoveFileToDataRoom,
    file: { name, type, originalType },
  } = chatSessionFile;

  const { t } = useTranslation();

  const { onFileAddToDataRoom, onFileRemove } = useContext(ChatFilesContext);

  const isDetailed = variant === 'detailed';

  const { options, isDisabled } = useChatFileMenuOptions({
    id: _id,
    name,
    type: originalType || type,
    isDataRoomFile,
    canMoveFileToDataRoom: hasDataRoomAccess && canMoveFileToDataRoom,
    canRemoveFile: canMoveFileToDataRoom,
    dataRoomId,
    dataRoomName,
    ownerId,
    userSide,
    onFileAddToDataRoom,
    onFileRemove,
    onRemoved,
    onSavedToDataRoom,
  });

  // with Data Room access the row reports where the file lives; without it there is no
  // such thing to report, so the row describes the file instead
  const secondLine = useMemo(() => {
    if (isDetailed) return getFileMetaLine(chatSessionFile, true);

    return hasDataRoomAccess
      ? t(isDataRoomFile ? 'inDataRoom' : 'notSavedToDataRoom')
      : getFileMetaLine(chatSessionFile);
  }, [isDetailed, hasDataRoomAccess, isDataRoomFile, chatSessionFile, t]);

  const classes = classNames(
    'chat-file-row',
    `chat-file-row--${variant}`,
    { 'chat-file-row--disabled': isDisabled },
    className,
  );

  const showMenu = getHasElementsInArray(options);

  return (
    <li className={classes}>
      <button
        type="button"
        className="chat-file-row__file"
        disabled={isDisabled}
        onClick={() => onOpen(_id, name)}
      >
        <MymeTypeIcon mimeType={originalType || type} />

        <span className="chat-file-row__text">
          <span className="chat-file-row__name">{name}</span>
          <span className="chat-file-row__meta">{secondLine}</span>
        </span>
      </button>

      {isDetailed && hasDataRoomAccess && (
        <span
          className={classNames('chat-file-row__status', {
            'chat-file-row__status--saved': isDataRoomFile,
          })}
        >
          {t(isDataRoomFile ? 'inDataRoom' : 'notSavedToDataRoom')}
        </span>
      )}

      {showMenu && (
        <ThreeDotsDropdown
          anchorToTrigger
          withBorder={false}
          alignment={ALIGNMENT.VERTICAL}
          options={options}
          className="chat-file-row__menu"
        />
      )}
    </li>
  );
};

export default ChatFileRow;
